import { useIsVisible } from '@folklore/hooks';
import { useUrlGenerator } from '@folklore/routes';

import useAuthor from '../../hooks/useAuthor';

import { useIsCapture } from '../../contexts/LayoutContext';
import AuthorSummary from '../summaries/AuthorSummary';
import SummaryRowBlock, { SummaryRowBlockProps } from './SummaryRowBlock';

export interface AuthorRowBlockProps extends SummaryRowBlockProps {
    slug: string;
    query?: Record<string, unknown> | null;
    withoutSubscribeButton?: boolean;
}

function AuthorRowBlock({
    slug,
    query = null,
    loading = 'lazy',
    withoutSubscribeButton = false,
    className = null,
    disabled = false,
    ...props
}: AuthorRowBlockProps) {
    const url = useUrlGenerator();
    const isCapture = useIsCapture();
    const isLazyLoading = loading === 'lazy' && !isCapture;
    const { ref, visible: isVisible } = useIsVisible({
        persist: true,
        disabled: !isLazyLoading,
    });
    const shouldLoad = !isLazyLoading || isVisible;
    const { author = null } = useAuthor(slug, {
        enabled: shouldLoad && !disabled,
    });
    const finalQuery =
        author !== null
            ? {
                  author: author.id,
                  ...query,
              }
            : null;

    const summary =
        author !== null
            ? {
                  ...author,
                  withSubscribeButton: !withoutSubscribeButton,
                  withLink: withoutSubscribeButton,
              }
            : null;

    return (
        <SummaryRowBlock
            url={url('author', { author: slug })}
            summary={summary}
            summaryComponent={AuthorSummary}
            query={finalQuery}
            disabled={author === null}
            loading={loading}
            ref={ref}
            className={className}
            summaryImagePosition="left"
            {...props}
        />
    );
}

export default AuthorRowBlock;
